import { useMemo } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { prepareReceipt, useSettlementReceipt } from './settlements.api';

/**
 * The receipt the terminal printed when it closed the batch.
 *
 * Rendered in a sandboxed frame: it is the terminal's own HTML, scripts
 * included, and none of it should run inside the portal.
 */
export function SettlementReceiptDialog({
  settlementId,
  open,
  onOpenChange,
}: {
  settlementId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation();
  const receipt = useSettlementReceipt(open ? settlementId : null);
  const html = useMemo(
    () => (receipt.data ? prepareReceipt(receipt.data) : null),
    [receipt.data]
  );

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          data-scrim="over"
          className={cn(
            'fixed inset-0 z-50 bg-black/70 backdrop-blur-sm',
            'data-[state=open]:animate-in data-[state=open]:fade-in-0',
            'data-[state=closed]:animate-out data-[state=closed]:fade-out-0'
          )}
        />
        <DialogPrimitive.Content
          className={cn(
            'bg-background fixed top-1/2 left-1/2 z-50 w-[calc(100%-2rem)] max-w-md -translate-x-1/2 -translate-y-1/2 rounded-xl border p-4 shadow-xl',
            'data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95',
            'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95'
          )}
        >
          <DialogPrimitive.Title className="mb-3 text-sm font-semibold">
            {t('settlements.receiptTitle')}
          </DialogPrimitive.Title>
          <DialogPrimitive.Description className="sr-only">
            {t('settlements.receiptTitle')}
          </DialogPrimitive.Description>

          {receipt.isPending ? (
            <Skeleton className="h-[65svh] w-full rounded-lg" />
          ) : receipt.error ? (
            <p className="text-destructive py-10 text-center text-sm">
              {receipt.error instanceof Error
                ? receipt.error.message
                : t('settlements.receiptFailed')}
            </p>
          ) : html ? (
            <iframe
              title={t('settlements.receiptTitle')}
              srcDoc={html}
              sandbox=""
              className="h-[65svh] w-full rounded-lg border bg-white"
            />
          ) : (
            <p className="text-muted-foreground py-10 text-center text-sm">
              {t('settlements.noReceipt')}
            </p>
          )}

          <DialogPrimitive.Close className="text-muted-foreground hover:text-foreground focus-visible:ring-ring absolute top-3 right-3 rounded p-1 focus-visible:ring-2 focus-visible:outline-none">
            <X className="size-4" />
            <span className="sr-only">{t('settlements.close')}</span>
          </DialogPrimitive.Close>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
